/**
 * ChatResponseSlots component for the Indigo design system.
 *
 * Displays a horizontal list of selectable time / option slots inside a chat
 * response bubble. Used when the assistant proposes a small set of compact
 * choices the user can pick from (e.g. available appointment times).
 *
 * Each slot renders as a sky-tinted pill button. An optional header text is
 * rendered above the list as Body XS in tertiary color.
 *
 * @example
 * ```tsx
 * <ChatResponseSlots
 *   header="Available time slots"
 *   slots={[
 *     { label: "9:00 AM", onClick: () => pick("09:00") },
 *     { label: "10:30 AM", onClick: () => pick("10:30") },
 *     { label: "1:00 PM", onClick: () => pick("13:00") },
 *   ]}
 * />
 * ```
 */
import { clsx } from "clsx";
import { forwardRef } from "react";
import { Body } from "../../Typography/Body";
import "./ChatResponseSlots.css";

export interface ChatResponseSlotItem {
  /** Visible label for the slot. */
  label: string;
  /** Click handler invoked when the slot is selected. */
  onClick?: () => void;
  /** When true, the slot is rendered in a disabled state. */
  disabled?: boolean;
  /** When true, the slot is rendered as already selected. */
  selected?: boolean;
  /** Optional test id for the slot button. */
  "data-testid"?: string;
}

export interface ChatResponseSlotsProps {
  /** Optional header rendered above the slot list. */
  header?: string;
  /** List of slot pills. */
  slots: ChatResponseSlotItem[];
  /** Additional CSS class applied to the root. */
  className?: string;
  /** Test id for the root element. */
  "data-testid"?: string;
}

export const ChatResponseSlots = forwardRef<
  HTMLDivElement,
  ChatResponseSlotsProps
>(({ header, slots, className, "data-testid": dataTestId }, ref) => {
  return (
    <div
      ref={ref}
      className={clsx("indigo-chat-response-slots", className)}
      data-testid={dataTestId}
    >
      {header && (
        <Body
          as="span"
          size="xs"
          className="indigo-chat-response-slots__header"
        >
          {header}
        </Body>
      )}
      <div className="indigo-chat-response-slots__list" role="list">
        {slots.map((slot, index) => (
          <button
            key={`${slot.label}-${index}`}
            type="button"
            role="listitem"
            className={clsx(
              "indigo-chat-response-slots__slot",
              slot.selected && "indigo-chat-response-slots__slot--selected",
            )}
            onClick={slot.onClick}
            disabled={slot.disabled}
            data-testid={slot["data-testid"]}
          >
            <Body
              as="span"
              size="xs"
              className="indigo-chat-response-slots__slot-label"
            >
              {slot.label}
            </Body>
          </button>
        ))}
      </div>
    </div>
  );
});

ChatResponseSlots.displayName = "ChatResponseSlots";

export default ChatResponseSlots;
